// Core packages
import {
  Component, Inject,
  OnDestroy, OnInit
} from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { File } from "src/app/shared/interfaces/file.interface";
import { PlatformNote } from "src/app/shared/models/platform-note/platform-note.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";

@Component({
  selector: "app-view-platform-note-dialog",
  templateUrl: "./view-platform-note-dialog.component.html",
  styleUrls: ["./view-platform-note-dialog.component.scss"]
})
export class ViewPlatformNoteDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  user: User = new User();
  lang = this.translateService.currentLang || this.translateService.defaultLang;

  // BEGIN - Add subUser form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.add"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  // END - Add subUser form

  dialogIsLoading = true;

  createdBy = '';
  createdAt = '';
  category = '';
  title = '';
  description = '';
  file: File;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<ViewPlatformNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public configService: ConfigService,
    private cookieService: CookieService,
    private translateService: TranslateService,
    private authService: AuthService,
    private apiService: ApiService
  ) {
    dialogRef.disableClose = true;
  }

  /**
   * Init component
   *
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // View data
    if (this.data && this.data.item) {
      const item: PlatformNote = this.data.item;
      const createdBy = item.createdBy as any;
      this.createdBy = `${createdBy.firstName} ${createdBy.lastName}`;
      if (
        item.createdAt && 
        moment(item.createdAt).isValid()
      ) {
        this.createdAt = moment(item.createdAt).format("DD/MM/YYYY hh:mm");
      }
      if (item.category) {
        switch (item.category) {
          case "subscription": 
            this.category = this.translateService.instant("platformNotes.categories.subscription"); 
          break;
          case "work-account":
            this.category = this.translateService.instant("platformNotes.categories.work-account");
            break;
          case "technical-operational":
            this.category = this.translateService.instant("platformNotes.categories.technical-operational");
            break;
          case "insertion":
            this.category = this.translateService.instant("platformNotes.categories.insertion");
            break;
          case "mapping":
            this.category = this.translateService.instant("platformNotes.categories.mapping");
            break;
          case "packaging":
            this.category = this.translateService.instant("platformNotes.categories.packaging");
            break;
          case "tagnfc":
            this.category = this.translateService.instant("platformNotes.categories.tagnfc");
            break;
          case "suppliers":
            this.category = this.translateService.instant("platformNotes.categories.suppliers");
            break;
          case "other":
            this.category = this.translateService.instant("platformNotes.categories.other");
            break;
        }
      }
      this.title = item.title;
      this.description = item.description;
      if (item.file && item.file.path) {
        this.file = item.file;
      }
    }

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
